import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { useExportEvents } from '@/api/hooks/useExportEvents';
import { ThreadExportEvent } from '@/api/hooks/useThreadExportEvents';
import { APIBaseChronos } from '@/api/hosts';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Progress } from '@/new/components/shadcn/progress';

interface ThreadExportProgressDialogProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  threadId: string;
  messageId: string;
  onDownload: () => Promise<void>;
  isCreating: boolean;
  error?: string;
}

export function ThreadExportProgressDialog({
  isOpen,
  onClose,
  caseId,
  threadId,
  messageId,
  onDownload,
  error,
}: ThreadExportProgressDialogProps) {
  const { matterId } = useParams();
  if (!matterId) return null;

  const [exportReady, setExportReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [processedDocuments, setProcessedDocuments] = useState(0);

  useExportEvents<ThreadExportEvent>({
    progressEndpoint: `${APIBaseChronos}/api/matter/${matterId}/subset/${caseId}/thread/${threadId}/message/${messageId}/export-progress`,
    onEvent: (event) => {
      if (event.type.includes('progress')) {
        setProgress(event.archiveProgress || 0);
        if (event.totalDocuments) setTotalDocuments(event.totalDocuments);
        if (event.processedDocuments !== undefined) setProcessedDocuments(event.processedDocuments);
      } else if (event.type.includes('complete')) {
        setProgress(100);
        setExportReady(true);
      }
    },
    enabled: isOpen && !exportReady,
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--file-archive" />
            {error ? 'Export Error' : exportReady ? 'Export Ready' : 'Creating Export'}
          </DialogTitle>
        </DialogHeader>

        {/* Error State */}
        {error && (
          <div className="flex min-h-64 flex-col items-center justify-center gap-4 py-8">
            <Icon className="lucide--circle-x size-16 text-red-500" />
            <div className="text-lg font-medium text-red-600">Error creating export</div>
            <div className="text-center text-gray-600">{error}</div>
          </div>
        )}

        {/* Ready State */}
        {!error && exportReady && (
          <div className="flex flex-col items-center justify-center gap-4">
            <Icon className="lucide--circle-check text-3xl text-green-500" />
            <div className="text-lg font-medium text-gray-700">Export ready for download</div>
            <div className="text-center text-gray-600">
              Kim Answer with source documents has been packaged.
              {totalDocuments > 0 && (
                <div className="mt-1 text-sm text-gray-500">
                  {totalDocuments} document{totalDocuments !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            <Button variant="primary" onClick={onDownload} className="w-full">
              <Icon className="lucide--download" />
              Download
            </Button>
          </div>
        )}

        {/* Creating/Progress State */}
        {!error && !exportReady && (
          <div className="flex min-h-64 flex-col items-center justify-center gap-4">
            <Icon className="lucide--file-archive text-3xl text-teal-500" />
            <div className="text-lg font-medium text-gray-700">Creating export...</div>
            <div className="w-full max-w-md space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} />
              {totalDocuments > 0 && (
                <p className="mt-4 text-center text-sm text-gray-600">
                  Processing {processedDocuments} of {totalDocuments} document
                  {totalDocuments !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            <div className="text-center text-sm text-balance text-gray-600">
              This may take a few minutes depending on the number and size of documents.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
